import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../services/authentication/authContext';
import { useSubscription } from '../../hooks/useSubscription';
import { Check, CreditCard, Lock, Loader2, AlertCircle } from 'lucide-react';
import { createStripeCheckoutSession, getSubscriptionPricing, SubscriptionPricing } from '../../services/subscription/subscriptionService';
import { fetchRestaurants, FrontendRestaurant } from '../../services/restaurant/restaurantService';
import { useTranslation } from 'react-i18next';
import { Header } from '../pages/landingpage/sections/Header';

type BillingPeriod = 'monthly' | 'yearly';

export default function PaymentPage() {
  const { backendUser, accountSettings } = useAuth();
  const { t } = useTranslation();
  const [loadError, setLoadError] = useState(false);
  const { isActive, loading } = useSubscription();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [pricing, setPricing] = useState<SubscriptionPricing | null>(null);
  const [pricingLoading, setPricingLoading] = useState(true);

  // Checkout needs only restaurant identities, never operational setup data.
  const [restaurants, setRestaurants] = useState<FrontendRestaurant[]>([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setRestaurantsLoading(true);
    fetchRestaurants().then(data => {
      if (!cancelled) { setRestaurants(data); setIsDataLoaded(true); }
    }).catch(err => { console.error('Failed to load checkout restaurants:', err); if (!cancelled) setLoadError(true); })
      .finally(() => { if (!cancelled) setRestaurantsLoading(false); });
    return () => { cancelled = true; };
  }, [backendUser?.id]);

  // Load pricing on mount
  // Keyed on presence, not the profile object the access poll replaces every 10s.
  const hasBackendUser = !!backendUser;
  useEffect(() => {
    if (hasBackendUser) {
      getSubscriptionPricing()
        .then(setPricing)
        .catch(err => { console.error('Failed to load pricing:', err); setLoadError(true); })
        .finally(() => setPricingLoading(false));
    }
  }, [hasBackendUser]);

  // Redirect to home if user already has active subscription
  if (!loading && isActive) {
    return <Navigate to={backendUser?.managed_customer ? (accountSettings.includes('account_setup') ? '/setup' : '/dashboard') : '/'} replace />;
  }

  // Show info message for staff users (only admin can subscribe)
  const isStaff = backendUser?.role === 'staff';

  // Get all restaurant IDs
  const restaurantIds = restaurants.map(r => r.id);
  const restaurantCount = restaurantIds.length;
  const canSubscribe = !restaurantsLoading && isDataLoaded && restaurantCount > 0 && !pricingLoading && pricing !== null;

  const features = [
    'Complete order history tracking',
    'Revenue analytics & insights',
    'Cost price management',
    'Price alerts & notifications',
    'Multi-restaurant support',
    'Real-time reporting',
    'Priority support'
  ];

  // Derive display prices from pricing (cents → euros)
  const monthlyPerRestaurant = pricing ? pricing.price_per_restaurant / 100 : 0;
  const yearlyPerRestaurant = monthlyPerRestaurant * 10; // 16% discount
  const monthlyEquivalent = yearlyPerRestaurant / 12;
  const savingsPerRestaurant = (monthlyPerRestaurant * 12) - yearlyPerRestaurant;
  const setupFee = pricing ? pricing.setup_fee / 100 : 0;

  const currentPricePerRestaurant = billingPeriod === 'monthly' ? monthlyPerRestaurant : yearlyPerRestaurant;
  const currentPeriod = billingPeriod === 'monthly' ? 'month' : 'year';
  const totalRecurring = currentPricePerRestaurant * restaurantCount;

  // Format number with thousands separator
  const formatPrice = (price: number) => {
    return price.toLocaleString('en-US');
  };

  return (
    <div className="relative overflow-hidden min-h-screen bg-landingPageBG dark:bg-landingPageBG-dark text-white dark:text-white">
      <Header />
      <div className="flex flex-col justify-center items-center min-h-screen px-5 md:px-8 py-20">
        {loadError && <p role="alert" className="mb-4 text-red-300 dark:text-red-300">{t('managedSignup.paymentLoadFailed')} <button className="underline min-h-[44px]" onClick={() => window.location.reload()}>{t('accountSetup.retry')}</button></p>}
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-sm px-3 py-1 text-xs text-white/70 dark:text-white/70 mb-6">
            <Lock className="w-3 h-3" />
            Subscription Required
          </div>

          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white dark:text-white mb-4">
            {isStaff ? 'Subscription Pending' : 'Unlock Full Access'}
          </h1>
          <p className="text-lg text-white/70 dark:text-white/70 max-w-2xl mx-auto">
            {isStaff
              ? 'Your account administrator needs to activate a subscription to unlock full access'
              : 'Get access to all features and start managing your restaurant operations more efficiently'
            }
          </p>
        </div>

        {/* Staff Info Message */}
        {isStaff ? (
          <div className="w-full max-w-xl mx-auto">
            <div className="rounded-2xl border border-white/10 dark:border-white/10 p-8 bg-white/5 dark:bg-white/5 backdrop-blur-sm">
              {/* Alert Icon */}
              <div className="mb-6 flex justify-center">
                <div className="h-16 w-16 rounded-full bg-amber-500/10 dark:bg-amber-500/10 flex items-center justify-center">
                  <AlertCircle className="w-10 h-10 text-amber-400 dark:text-amber-400" />
                </div>
              </div>

              {/* Message */}
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white dark:text-white mb-4">
                  Waiting for Subscription
                </h2>
                <p className="text-white/70 dark:text-white/70 mb-6">
                  Your account is currently on hold. The account administrator needs to activate or restart the subscription to grant you access to Culinary Cloud features.
                </p>
                <div className="p-4 bg-white/5 dark:bg-white/5 rounded-xl border border-white/10 dark:border-white/10">
                  <p className="text-sm text-white/80 dark:text-white/80">
                    <strong>Note:</strong> Only the account administrator can subscribe and manage payments. Please contact them to activate the subscription.
                  </p>
                </div>
              </div>

              {/* Features Preview */}
              <div className="mt-8 pt-6 border-t border-white/10 dark:border-white/10">
                <p className="text-sm font-semibold text-white dark:text-white mb-4 text-center">
                  Once activated, you'll get access to:
                </p>
                <div className="space-y-2">
                  {features.slice(0, 5).map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-400 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-white/70 dark:text-white/70 text-sm">{feature}</span>
                    </div>
                  ))}
                  <div className="flex items-start gap-2">
                    <span className="text-white/50 dark:text-white/50 text-sm ml-6">...and more</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Billing Period Toggle */}
            <div className="mb-8">
          <div className="inline-flex items-center gap-2 p-1 rounded-2xl border border-white/10 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-sm">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-xl text-sm font-medium transition-all ${
                billingPeriod === 'monthly'
                  ? 'bg-accent text-white shadow-md'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-xl text-sm font-medium transition-all relative ${
                billingPeriod === 'yearly'
                  ? 'bg-accent text-white shadow-md'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Yearly
              <span className="absolute -top-2 -right-2 bg-emerald-500 dark:bg-emerald-500 text-white dark:text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                Save 16%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Card */}
        <div className="w-full max-w-xl mx-auto">
          <div className="rounded-2xl border border-white/10 dark:border-white/10 p-8 bg-white/5 dark:bg-white/5 backdrop-blur-sm">
            {pricingLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-white/50 dark:text-white/50" />
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white dark:text-white mb-2">
                    Culinary Cloud Access - {billingPeriod === 'monthly' ? 'Monthly' : 'Yearly'}
                  </h2>
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-white dark:text-white">
                        €{formatPrice(totalRecurring)}
                      </span>
                      <span className="text-white/70 dark:text-white/70">
                        /{currentPeriod}
                      </span>
                    </div>
                    <div className="text-sm text-white/50 dark:text-white/50">
                      {restaurantCount} restaurant{restaurantCount !== 1 ? 's' : ''} × €{formatPrice(currentPricePerRestaurant)}/{currentPeriod} (excl. VAT)
                    </div>
                    {billingPeriod === 'yearly' && (
                      <div className="mt-2 inline-flex items-center gap-2 text-sm">
                        <span className="text-emerald-400 dark:text-emerald-400 font-semibold">
                          €{formatPrice(parseFloat((monthlyEquivalent * restaurantCount).toFixed(2)))}/month
                        </span>
                        <span className="text-white/50 dark:text-white/50">
                          (Save €{formatPrice(savingsPerRestaurant * restaurantCount)}/year)
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* One-time setup fee */}
                {setupFee > 0 && (
                  <div className="mb-6 p-4 bg-white/5 dark:bg-white/5 rounded-xl border border-white/10 dark:border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-white/70 dark:text-white/70">One-time setup fee</span>
                      <span className="text-sm font-semibold text-white dark:text-white">€{formatPrice(setupFee)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-white/10 dark:border-white/10">
                      <span className="text-sm font-semibold text-white dark:text-white">Due today</span>
                      <span className="text-sm font-semibold text-white dark:text-white">€{formatPrice(totalRecurring + setupFee)} (excl. VAT)</span>
                    </div>
                    <div className="text-xs text-white/40 dark:text-white/40 mt-1 text-right">
                      Then €{formatPrice(totalRecurring)}/{currentPeriod} recurring
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="h-5 w-5 rounded-full bg-emerald-500/10 dark:bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-400 dark:text-emerald-400" />
                  </div>
                  <span className="text-white/80 dark:text-white/80 text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <button
              onClick={async () => {
                if (restaurantIds.length === 0) {
                  alert('No restaurants found. Please create a restaurant first.');
                  return;
                }

                try {
                  setIsProcessing(true);
                  const session = await createStripeCheckoutSession(billingPeriod, restaurantIds);
                  // Redirect to Stripe checkout
                  window.location.href = session.url;
                } catch (error) {
                  console.error('Failed to create checkout session:', error);
                  alert('Failed to start checkout. Please try again.');
                  setIsProcessing(false);
                }
              }}
              disabled={isProcessing || !canSubscribe}
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base font-semibold bg-accent dark:bg-accent-dark text-white dark:text-white hover:bg-accent/90 dark:hover:bg-accent-dark/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : restaurantsLoading || pricingLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Subscribe {billingPeriod === 'monthly' ? 'Monthly' : 'Yearly'}
                </>
              )}
            </button>

            <p className="text-xs text-white/50 dark:text-white/50 text-center mt-4">
              Secure payment powered by Stripe • Cancel anytime
            </p>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 pt-8 border-t border-white/10 dark:border-white/10 w-full max-w-xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-8 text-white/50 dark:text-white/50 text-sm">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>SSL Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span>Secure Payments</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              <span>GDPR Compliant</span>
            </div>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
